import React, { useState } from 'react';
import { Layout, Menu, theme, Drawer, Button, Grid, Modal } from 'antd';
import {
  MenuOutlined,
  ShoppingCartOutlined,
  AppstoreOutlined,
  OrderedListOutlined,
  DollarOutlined,
  SettingOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  CarOutlined,
  LogoutOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import CategoryManagement from '../components/admin/CategoryManagement';
import ProductManagement from '../components/admin/ProductManagement';
import OrderManagement from '../components/admin/OrderManagement';
import StatisticsDashboard from '../components/admin/StatisticsDashboard';
import CashierRegister from '../components/admin/CashierRegister';
import IngredientManagement from '../components/admin/IngredientManagement';
import ExpenseManagement from '../components/admin/ExpenseManagement';
import SiteSettings from '../components/admin/SiteSettings';
import ZoneManagement from '../components/admin/ZoneManagement';
import UserManagement from '../components/admin/UserManagement';
import DeliveryDriverView from '../components/admin/DeliveryDriverView';
import { useAuth } from '../contexts/AuthContext';

const { Header, Content, Sider } = Layout;
const { useBreakpoint } = Grid;

const normalizeRole = (role: string) => (role === 'user' ? 'agent' : role);

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = normalizeRole(user.role || '');
  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.name ||
    user.email ||
    'Utilisateur';

  const defaultMenu =
    userType === 'livreur' ? 'deliveries' :
    userType === 'agent' ? 'orders' :
    'statistiques';

  const [selectedMenu, setSelectedMenu] = useState(defaultMenu);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const screens = useBreakpoint();
  const isDesktop = screens.md;

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const menuItems = [
    {
      key: 'statistiques',
      label: 'Statistiques',
      icon: <OrderedListOutlined />,
      roles: ['admin'],
    },
    {
      key: 'products',
      label: 'Gestion des produits',
      icon: <ShoppingCartOutlined />,
      roles: ['admin'],
    },
    {
      key: 'categories',
      label: 'Gestion des catégories',
      icon: <AppstoreOutlined />,
      roles: ['admin'],
    },
    {
      key: 'ingredients',
      label: 'Ingrédients',
      icon: <AppstoreOutlined />,
      roles: ['admin'],
    },
    {
      key: 'zones',
      label: 'Zones de livraison',
      icon: <EnvironmentOutlined />,
      roles: ['admin'],
    },
    {
      key: 'users',
      label: 'Gérer les utilisateurs',
      icon: <TeamOutlined />,
      roles: ['admin'],
    },
    {
      key: 'orders',
      label: 'Gestion des commandes',
      icon: <OrderedListOutlined />,
      roles: ['admin', 'agent'],
    },
    {
      key: 'cashier',
      label: 'Caisse',
      icon: <AppstoreOutlined />,
      roles: ['admin', 'agent'],
    },
    {
      key: 'expenses',
      label: 'Gestion des dépenses',
      icon: <DollarOutlined />,
      roles: ['admin', 'agent'],
    },
    {
      key: 'deliveries',
      label: 'Mes livraisons',
      icon: <CarOutlined />,
      roles: ['livreur', 'admin'],
    },
    {
      key: 'settings',
      label: 'Paramètres site',
      icon: <SettingOutlined />,
      roles: ['admin'],
    },
  ];

  const filteredMenu = menuItems.filter((item) => item.roles.includes(userType));

  const handleLogout = () => {
    Modal.confirm({
      title: 'Se déconnecter ?',
      icon: <ExclamationCircleOutlined />,
      content: 'Êtes-vous sûr de vouloir vous déconnecter de votre session ?',
      okText: 'Oui, me déconnecter',
      cancelText: 'Annuler',
      okType: 'danger',
      onOk: () => {
        logout();
        navigate('/admin/login');
      },
    });
  };

  const renderContent = () => {
    if (!user) return null;

    if (userType === 'livreur') {
      return <DeliveryDriverView />;
    }

    if (userType === 'agent') {
      switch (selectedMenu) {
        case 'orders':
          return <OrderManagement />;
        case 'cashier':
          return <CashierRegister />;
        case 'expenses':
          return <ExpenseManagement />;
        default:
          return <OrderManagement />;
      }
    }

    switch (selectedMenu) {
      case 'products':
        return <ProductManagement />;
      case 'categories':
        return <CategoryManagement />;
      case 'ingredients':
        return <IngredientManagement />;
      case 'zones':
        return <ZoneManagement />;
      case 'users':
        return <UserManagement />;
      case 'orders':
        return <OrderManagement />;
      case 'statistiques':
        return <StatisticsDashboard />;
      case 'cashier':
        return <CashierRegister />;
      case 'expenses':
        return <ExpenseManagement />;
      case 'deliveries':
        return <DeliveryDriverView />;
      case 'settings':
        return <SiteSettings />;
      default:
        return <StatisticsDashboard />;
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          position: 'fixed',
          width: '100%',
          zIndex: 10,
          padding: isDesktop ? '0 24px' : '0 12px'
        }}
      >
        {!isDesktop && (
          <Button
            type="text"
            icon={<MenuOutlined style={{ color: 'white', fontSize: 20 }} />}
            onClick={() => setMobileMenuVisible(true)}
          />
        )}
        <h1
          style={{
            color: 'white',
            margin: 0,
            fontSize: isDesktop ? '1.5rem' : '1.1rem',
            marginLeft: isDesktop ? 0 : 8,
            flex: 1,
          }}
        >
          Tableau de bord
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isDesktop && (
            <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
              {displayName}
            </span>
          )}
          <Button
            type="primary"
            danger
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            size={isDesktop ? 'middle' : 'small'}
          >
            {isDesktop ? 'Déconnexion' : ''}
          </Button>
        </div>
      </Header>

      <Layout style={{ marginTop: 64 }}>
        <Drawer
          title="Menu"
          placement="left"
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          width={250}
          bodyStyle={{ padding: 0 }}
          headerStyle={{
            background: '#001529',
            color: 'white',
            border: 'none'
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={[selectedMenu]}
            items={filteredMenu}
            onClick={({ key }) => {
              setSelectedMenu(key);
              setMobileMenuVisible(false);
            }}
            style={{
              borderRight: 'none',
              height: '100%'
            }}
            theme="dark"
          />
        </Drawer>

        {isDesktop && (
          <Sider
            width={200}
            style={{
              background: colorBgContainer,
              position: 'fixed',
              left: 0,
              top: 64,
              bottom: 0,
              paddingTop: '50px'
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[selectedMenu]}
              items={filteredMenu}
              onClick={({ key }) => setSelectedMenu(key)}
              style={{
                height: '100%',
                borderRight: 0,
                paddingTop: '20px'
              }}
              theme="dark"
            />
          </Sider>
        )}

        <Layout
          style={{
            padding: isDesktop ? '24px' : '12px',
            minHeight: 'calc(100vh - 64px)',
            marginLeft: isDesktop ? 200 : 0
          }}
        >
          <Content
            style={{
              padding: isDesktop ? 24 : 12,
              margin: 0,
              minHeight: 280,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
              overflow: 'auto'
            }}
          >
            {renderContent()}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default AdminDashboard;
